import { gql } from "graphql-request";
import { z } from "zod";
import { Image, ImageFragment } from "../image";
import { PollBase } from "./carouselEventPollEntry";

export const TextBlock = z.object({
  __typename: z.literal("TextBlock"),
  title: z.string().nullable(),
  text: z
    .object({
      json: z.unknown(),
    })
    .nullable(),
  accordion: z.enum(["yes", "no"]).nullable(),
});

export type TextBlock = z.infer<typeof TextBlock>;

export const TextBlockFragment = gql`
  fragment TextBlockFragment on TextBlock {
    title
    text {
      json
    }
    accordion
  }
`;

export const ImageBlock = z.object({
  __typename: z.literal("ImageBlock"),
  image: Image.nullable(),
  imageEn: Image.nullable(),
  author: z.string().nullable(),
  fullWidth: z.enum(["yes", "no"]).nullable(),
});

export type ImageBlock = z.infer<typeof ImageBlock>;

export const ImageBlockFragment = gql`
  fragment ImageBlockFragment on ImageBlock {
    image {
      ...ImageFragment
    }
    imageEn: image(locale: "en") {
      ...ImageFragment
    }
    author
    fullWidth
  }

  ${ImageFragment}
`;

export const FeatureBlock = z.object({
  __typename: z.literal("FeatureBlock"),
  title: z.string().nullable(),
  featureText: z
    .object({
      json: z.unknown(),
    })
    .nullable(),
  featureThumbnail: Image.nullable(),
  featureThumbnailEn: Image.nullable(),
  featureThumbnailPosition: z.enum(["left", "right"]).nullable(),
  accordion: z.enum(["yes", "no"]).nullable(),
});

export type FeatureBlock = z.infer<typeof FeatureBlock>;

export const FeatureBlockFragment = gql`
  fragment FeatureBlockFragment on FeatureBlock {
    title
    featureText {
      json
    }
    featureThumbnail {
      ...ImageFragment
    }
    featureThumbnailEn: featureThumbnail(locale: "en") {
      ...ImageFragment
    }
    featureThumbnailPosition
    accordion
  }
  ${ImageFragment}
`;

export const VideoBlock = z.object({
  __typename: z.literal("VideoBlock"),
  title: z.string().nullable(),
  videoUrl: z.string().nullable(),
  videoThumbnail: Image.nullable(),
  videoThumbnailEn: Image.nullable(),
});

export type VideoBlock = z.infer<typeof VideoBlock>;

export const VideoBlockFragment = gql`
  fragment VideoBlockFragment on VideoBlock {
    title
    videoUrl
    videoThumbnail {
      ...ImageFragment
    }
    videoThumbnailEn: videoThumbnail(locale: "en") {
      ...ImageFragment
    }
  }

  ${ImageFragment}
`;

export const PollBlock = PollBase.extend({
  __typename: z.literal("PollBlock"),
});

export type PollBlock = z.infer<typeof PollBlock>;

export const PollBlockFragment = gql`
  fragment PollBlockFragment on PollBlock {
    pollTitle

    pollOptions
  }

  ${ImageFragment}
`;

const LeaderboardColumn = z.object({
  id: z.string(),
  title: z.string(),
  color: z.string().optional(),
  image: z
    .object({
      id: z.string(),
      title: z.string(),
      width: z.number(),
      height: z.number(),
      url: z.string(),
    })
    .optional(),
});

export const LeaderboardConfiguration = z
  .object({
    useRankColumn: z.boolean(),
    showColumnTitles: z.boolean(),
    columns: z.array(LeaderboardColumn),
  })
  .nullable();

export const LeaderboardBlock = z.object({
  __typename: z.literal("LeaderboardBlock"),
  title: z.string().nullable(),
  defaultRows: z.number().nullable(),
  maximumRows: z.number().nullable(),
  leaderboardConfiguration: LeaderboardConfiguration,
  leaderboardData: z.array(z.array(z.string().or(z.number()))).nullable(),
});

export type LeaderboardBlock = z.infer<typeof LeaderboardBlock>;

export const LeaderboardBlockFragment = gql`
  fragment LeaderboardBlockFragment on LeaderboardBlock {
    title
    defaultRows
    maximumRows
    leaderboardConfiguration
    leaderboardData
  }

  ${ImageFragment}
`;
